/* @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useCallback, useEffect, useRef } from 'react';

import type { Boundary } from '@floating-ui/react';
import classNames from 'classnames';
import type { PlayerWithPlugins } from 'video.js';

import { isAiDubbingEnabled } from 'js/lib/MachineTranslationExperiments';
import UserAgent from 'js/lib/useragent';

import { breakpoints } from '@coursera/cds-core';

import type { QuizQuestionPrompt } from 'bundles/compound-assessments/types/FormParts';
// eslint-disable-next-line @coursera/custom-rules/no-private-bundle-imports
import { useDubbingContext } from 'bundles/item-lecture/providers/DubbingProvider';
import AudioDubbingNudgeTooltip from 'bundles/video-player/components/AudioDubbingNudgeTooltip';
import FullscreenToggle from 'bundles/video-player/components/FullscreenToggle';
import IosFullscreenToggle from 'bundles/video-player/components/IosFullscreenToggle';
import PlayToggle from 'bundles/video-player/components/PlayToggle';
import PlaybackRateControl from 'bundles/video-player/components/PlaybackRateControl';
import { SeekBackwardControl, SeekForwardControl } from 'bundles/video-player/components/SeekControls';
import VideoTimeDisplay from 'bundles/video-player/components/VideoTimeDisplay';
import { useAutoplayToggle } from 'bundles/video-player/components/hooks/useAutoplayToggle';
import { useDubbing } from 'bundles/video-player/components/hooks/useDubbing';
import { useDubbingNudge } from 'bundles/video-player/components/hooks/useDubbingNudge';
import { usePlaybackRateControls } from 'bundles/video-player/components/hooks/usePlaybackRateControls';
import { useSubtitleMenuControls } from 'bundles/video-player/components/hooks/useSubtitleMenuControls';
import { useVideoQualityControls } from 'bundles/video-player/components/hooks/useVideoQualityControls';
import ModernSettingsMenuPopup from 'bundles/video-player/components/modernSettingsMenu/ModernSettingsMenuPopup';
import VideoProgressBar from 'bundles/video-player/components/progressBar/VideoProgressBar';
import VideoSettingsMenu from 'bundles/video-player/components/settingsMenu/VideoSettingsMenu';
import VolumeMenu from 'bundles/video-player/components/volumeMenu/VolumeMenu';
import {
  MODERN_VIDEO_PLAYER_CONTROL_BAR_HEIGHT,
  VIDEOPLAYER_FOCUS_OUTLINE_THICKNESS,
} from 'bundles/video-player/constants';
import type { DubbedSources, DubbedSubtitlesVtt, SourcesByResolution } from 'bundles/video-player/types/VideoContent';
import type { VideoPlayerEventingV3UIFunction, VideoPopup } from 'bundles/video-player/types/VideoPlayer';
import userPreferences from 'bundles/video-player/utils/userPreferences';
import type { InVideoQuestion } from 'bundles/video-quiz/index';

export type Props = {
  courseId?: string;
  defaultSubtitleLanguage: string;
  visible: boolean;
  visiblePopup: VideoPopup;
  setVisiblePopup: (popup: VideoPopup) => void;
  player: PlayerWithPlugins;
  videoQuizQuestions: InVideoQuestion<QuizQuestionPrompt>[] | null;
  onChangeFullScreen: (isFullscreen: boolean) => void;
  trackVideoPlayerClickEvent: VideoPlayerEventingV3UIFunction;
  trackVideoPlayerViewEvent: VideoPlayerEventingV3UIFunction;
  trimmedStartTimeSeconds: number;
  enableHls?: boolean;
  videoBoundaryElement?: Boundary;
  dubbedSources?: DubbedSources;
  originalSources: SourcesByResolution;
  dubbedSubtitlesVtt?: DubbedSubtitlesVtt;
  hlsSource?: string;
};

const styles = {
  controlBar: (controlBarHeight: number) => css`
    display: flex;
    align-items: center;
    flex-direction: column;
    position: absolute;
    bottom: 0;
    width: 100%;
    opacity: 0;
    height: 0;
    transition: height 0s 0.2s, opacity 0.2s linear;
    color: #dedede;

    &.visible {
      opacity: 1;
      height: ${controlBarHeight}px;
      transition: opacity 0.2s linear;

      ${breakpoints.down('xs')} {
        min-height: ${controlBarHeight}px;
        height: auto;
      }
    }

    &::after {
      position: absolute;
      bottom: 0;
      left: 0;
      width: 100%;
      height: 98px;
      content: '';
      z-index: -99;

      /* Manual CSS gradient easing so we're not left with a harsh edge
      Generated by https://larsenwork.com/easing-gradients
      For more info, see https://css-tricks.com/easing-linear-gradients/ */
      background-image: linear-gradient(
        to bottom,
        hsl(0deg 0% 0% / 0%) 0%,
        hsl(0deg 0% 0% / 0.8%) 8.1%,
        hsl(0deg 0% 0% / 3.2%) 15.5%,
        hsl(0deg 0% 0% / 6.9%) 22.5%,
        hsl(0deg 0% 0% / 11.6%) 29%,
        hsl(0deg 0% 0% / 17.1%) 35.3%,
        hsl(0deg 0% 0% / 23.2%) 41.2%,
        hsl(0deg 0% 0% / 29.7%) 47.1%,
        hsl(0deg 0% 0% / 36.3%) 52.9%,
        hsl(0deg 0% 0% / 42.8%) 58.8%,
        hsl(0deg 0% 0% / 48.9%) 64.7%,
        hsl(0deg 0% 0% / 54.4%) 71%,
        hsl(0deg 0% 0% / 59.1%) 77.5%,
        hsl(0deg 0% 0% / 62.8%) 84.5%,
        hsl(0deg 0% 0% / 65.2%) 91.9%,
        hsl(0deg 0% 0% / 66%) 100%
      );
    }
  `,
  controlBarAfter: (controlBarHeight: number) => css`
    &::after {
      position: absolute;
      bottom: 0;
      left: 0;
      width: 100%;
      height: ${controlBarHeight + 2}px;
      content: '';
      z-index: -99;

      /* Manual CSS gradient easing so we're not left with a harsh edge
      Generated by https://larsenwork.com/easing-gradients
      For more info, see https://css-tricks.com/easing-linear-gradients/ */
      background-image: linear-gradient(
        to top,
        hsl(0deg 0% 0% / 48%) 0%,
        hsl(0deg 0% 0% / 47.7%) 8.1%,
        hsl(0deg 0% 0% / 46.8%) 15.5%,
        hsl(0deg 0% 0% / 45.5%) 22.5%,
        hsl(0deg 0% 0% / 43.8%) 29%,
        hsl(0deg 0% 0% / 41.8%) 35.3%,
        hsl(0deg 0% 0% / 39.6%) 41.2%,
        hsl(0deg 0% 0% / 37.2%) 47.1%,
        hsl(0deg 0% 0% / 34.8%) 52.9%,
        hsl(0deg 0% 0% / 32.4%) 58.8%,
        hsl(0deg 0% 0% / 30.2%) 64.7%,
        hsl(0deg 0% 0% / 28.2%) 71%,
        hsl(0deg 0% 0% / 26.5%) 77.5%,
        hsl(0deg 0% 0% / 25.2%) 84.5%,
        hsl(0deg 0% 0% / 24.3%) 91.9%,
        hsl(0deg 0% 0% / 24%) 100%
      );
    }
  `,
  iconContainer: css`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    height: 100%;
    position: relative;
    width: 100%;

    /* Needs padding to prevent selection outlines from being cut off */
    padding: ${VIDEOPLAYER_FOCUS_OUTLINE_THICKNESS}px 8px;
  `,
  lastLeftControl: css`
    margin-right: auto;
  `,
};

const ControlBar = ({
  courseId = '',
  setVisiblePopup,
  visible,
  player,
  visiblePopup,
  defaultSubtitleLanguage,
  videoQuizQuestions,
  onChangeFullScreen,
  trackVideoPlayerClickEvent,
  trackVideoPlayerViewEvent,
  trimmedStartTimeSeconds,
  enableHls,
  videoBoundaryElement,
  dubbedSources,
  originalSources,
  dubbedSubtitlesVtt,
  hlsSource,
}: Props) => {
  const settingsMenuRef = useRef<HTMLDivElement | null>(null);
  const settingsMenuPopupRef = useRef<HTMLDivElement | null>(null);
  const settingsMenuButtonRef = useRef<HTMLButtonElement | null>(null);
  const { defaultLanguageKey } = useDubbingContext();
  const subtitlesMenuRef = useRef<HTMLDivElement | null>(null);
  const subtitlesMenuPopupRef = useRef<HTMLDivElement | null>(null);
  const subtitlesMenuButtonRef = useRef<HTMLButtonElement | null>(null);

  const volumeMenuRef = useRef<HTMLElement | null>(null);

  const userAgent = new UserAgent();

  const settingsMenuPopupName = 'settings';
  const subtitleMenuPopupName = 'subtitle';
  const volumeMenuPopupName = 'volume';

  const isSettingsMenuPopupVisible = visiblePopup === settingsMenuPopupName;
  const isSubtitleMenuPopupVisible = visiblePopup === subtitleMenuPopupName;

  const refDoesNotContainTarget = useCallback<(...args: $TSFixMe[]) => $TSFixMe>(
    (target: HTMLElement | HTMLDivElement) =>
      (ref: React.MutableRefObject<HTMLElement | HTMLDivElement | null> | undefined) =>
        ref?.current ? !ref.current.contains(target) : true,
    []
  );

  const closePopupMenu = useCallback<() => $TSFixMe>(() => {
    setVisiblePopup(null);
  }, [setVisiblePopup]);

  useEffect(() => {
    const dismissAllMenusExcept = (event: MouseEvent) => {
      const { target } = event;

      const doesNotContainTarget = refDoesNotContainTarget(target as HTMLElement);
      const hidePopups =
        doesNotContainTarget(settingsMenuRef) &&
        doesNotContainTarget(settingsMenuPopupRef) &&
        doesNotContainTarget(subtitlesMenuRef) &&
        doesNotContainTarget(subtitlesMenuPopupRef) &&
        doesNotContainTarget(volumeMenuRef);

      if (hidePopups) {
        setVisiblePopup(null);
      }
    };

    const closeAndFocusMenuButton = (buttonRef: React.RefObject<HTMLButtonElement>) => {
      closePopupMenu();
      buttonRef.current?.focus();
    };

    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (isSettingsMenuPopupVisible) {
          closeAndFocusMenuButton(settingsMenuButtonRef);
        }

        if (isSubtitleMenuPopupVisible) {
          closeAndFocusMenuButton(subtitlesMenuButtonRef);
        }
      }
    };

    document.addEventListener('mousedown', dismissAllMenusExcept);

    // add event listener when subtitle/settings menu are visible
    if (isSettingsMenuPopupVisible || isSubtitleMenuPopupVisible) {
      document.addEventListener('keydown', handleKeyPress);
    } else {
      document.removeEventListener('keydown', handleKeyPress);
    }

    return () => {
      document.removeEventListener('mousedown', dismissAllMenusExcept);
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [
    setVisiblePopup,
    refDoesNotContainTarget,
    closePopupMenu,
    isSettingsMenuPopupVisible,
    isSubtitleMenuPopupVisible,
  ]);

  const toggleMenuPopup = (menuName: string) => {
    setVisiblePopup(visiblePopup === menuName ? null : (menuName as VideoPopup));
  };

  const pauseVideoOnlyOnMobile = () => {
    const viewportWidth = document.documentElement.clientWidth;

    // We only want to pause the video if the viewport is XS / mobile views,
    // since it aligns with when we trigger the full screen popup menu overlay in SettingsMenuPopup and SubtitleMenuPopup
    if (viewportWidth <= breakpoints.values.sm) {
      player.pause();
    }
  };
  const onSettingsMenuClick = () => {
    toggleMenuPopup(settingsMenuPopupName);
    pauseVideoOnlyOnMobile();
    trackVideoPlayerClickEvent('video_settings_menu', {});
  };

  const setVolumeMenuPopup = () => {
    toggleMenuPopup(volumeMenuPopupName);
  };

  const assignVolumeMenuRef = (node: HTMLElement | null) => {
    volumeMenuRef.current = node;
  };

  const { currentPlaybackRate, supportedPlaybackRates, onPlaybackRateClick } = usePlaybackRateControls(
    player,
    trackVideoPlayerClickEvent
  );
  const {
    currentResolution,
    currentResolutionOption,
    supportedResolutions,
    onResolutionOptionClick,
    setCurrentResolutionOption,
    setCurrentResolution,
  } = useVideoQualityControls({ player, enableHls, hlsSource });

  const getDubbedSubtitleKeys = () => {
    return Object.keys(dubbedSubtitlesVtt ?? {});
  };

  const {
    shouldShowNudge,
    setShouldShowNudge,
    nudgeLanguageCode,
    shouldShowNudgeOnSubtitleChange,
    shouldShowNudgeOnMatchingUserLanguage,
  } = useDubbingNudge({
    getDubbedSubtitleKeys,
  });

  const inAiDubbingExpt = isAiDubbingEnabled(courseId);
  const hasDubbingPreferenceAndInAiDubbingExpt =
    userPreferences.get('audioLanguage') !== 'none' && !!userPreferences.get('audioLanguage') && inAiDubbingExpt;

  const {
    selectedAudioLanguage,
    onDubbedAudioChoiceClick,
    getDubbedLanguagesList,
    handleResolutionChangeForDubbedAudio,
  } = useDubbing({
    originalSources,
    dubbedSources,
    player,
    defaultLanguageKey,
    setShouldShowNudge,
    getDubbedSubtitleKeys,
    shouldShowNudgeOnMatchingUserLanguage,
    currentResolution,
    setCurrentResolutionOption,
    setCurrentResolution,
    enableHls,
    hlsSource,
  });

  const ensurePlayerHasInitializedVideoSettings =
    currentResolution !== null && currentPlaybackRate !== null && supportedResolutions !== null;

  const { onSubtitleLanguageChoiceClick, onSubtitleOffClick, currentTrack, tracks } = useSubtitleMenuControls({
    selectedAudioLanguage,
    defaultLanguageKey,
    defaultSubtitleLanguage,
    player,
    trackVideoPlayerClickEvent,
    shouldShowNudgeOnSubtitleChange,
  });

  const { autoplay, handleToggleSwitchClick } = useAutoplayToggle({
    subscribeToAutoplayChanges: (callback) => player.on('autoplaychange', callback),
    unsubscribeFromAutoplayChanges: (callback) => player.off('autoplaychange', callback),
    triggerAutoplayChangePlayerEvent: () => player.trigger('autoplaychange'),
  });

  return (
    <>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div
        data-testid="video-control-bar"
        css={[
          styles.controlBar(MODERN_VIDEO_PLAYER_CONTROL_BAR_HEIGHT),
          styles.controlBarAfter(MODERN_VIDEO_PLAYER_CONTROL_BAR_HEIGHT),
        ]}
        className={classNames('rc-ControlBar', { visible })}
        onClick={(e) => {
          e.stopPropagation();
        }}
        onDoubleClick={(e) => {
          e.stopPropagation();
        }}
      >
        <VideoProgressBar
          player={player}
          videoQuizQuestions={videoQuizQuestions}
          trackVideoPlayerClickEvent={trackVideoPlayerClickEvent}
          trimmedStartTimeSeconds={trimmedStartTimeSeconds}
        />
        <div className="icon-container" css={styles.iconContainer}>
          <PlayToggle
            player={player}
            trackVideoPlayerClickEvent={trackVideoPlayerClickEvent}
            videoBoundaryElement={videoBoundaryElement}
          />
          <VolumeMenu
            player={player}
            setVolumeMenuPopup={setVolumeMenuPopup}
            assignRef={assignVolumeMenuRef}
            trackVideoPlayerClickEvent={trackVideoPlayerClickEvent}
          />
          <SeekBackwardControl
            player={player}
            trackVideoPlayerClickEvent={trackVideoPlayerClickEvent}
            videoBoundaryElement={videoBoundaryElement}
          />
          <VideoTimeDisplay player={player} trimmedStartTimeSeconds={trimmedStartTimeSeconds} />
          <span css={styles.lastLeftControl}>
            <SeekForwardControl
              player={player}
              trackVideoPlayerClickEvent={trackVideoPlayerClickEvent}
              videoBoundaryElement={videoBoundaryElement}
              css={styles.lastLeftControl}
            />
          </span>
          <PlaybackRateControl
            trackVideoPlayerClickEvent={trackVideoPlayerClickEvent}
            onPlaybackRateClick={onPlaybackRateClick}
            currentPlaybackRate={currentPlaybackRate}
            videoBoundaryElement={videoBoundaryElement}
          />

          {ensurePlayerHasInitializedVideoSettings && (
            <VideoSettingsMenu
              ref={settingsMenuRef}
              menuPopupVisible={isSettingsMenuPopupVisible}
              onSettingsMenuClick={onSettingsMenuClick}
              buttonRef={settingsMenuButtonRef}
              videoBoundaryElement={videoBoundaryElement}
            />
          )}
          {/* iOS browsers don’t fully support the standard Fullscreen API, so a custom IosFullscreenToggle component is
          used for iOS devices to handle fullscreen using the player’s native features. */}
          {userAgent.isIOS ? (
            <IosFullscreenToggle player={player} trackVideoPlayerClickEvent={trackVideoPlayerClickEvent} />
          ) : (
            <FullscreenToggle onChangeFullScreen={onChangeFullScreen} videoBoundaryElement={videoBoundaryElement} />
          )}
        </div>
      </div>
      <AudioDubbingNudgeTooltip
        visible={shouldShowNudge && selectedAudioLanguage.languageKey === defaultLanguageKey}
        languageCode={nudgeLanguageCode}
        onDubbedAudioChoiceClick={onDubbedAudioChoiceClick}
        trackVideoPlayerClickEvent={trackVideoPlayerClickEvent}
        playerHasLoaded={Boolean(player)}
        trackVideoPlayerViewEvent={trackVideoPlayerViewEvent}
      />
      {/* We needed to render these popups outside of the ControlBar and outside of their respective menu components
       so that they're able to take up a full screen overlay in small viewports. */}
      {ensurePlayerHasInitializedVideoSettings && (
        <ModernSettingsMenuPopup
          trackVideoPlayerClickEvent={trackVideoPlayerClickEvent}
          enableHls={enableHls && !hasDubbingPreferenceAndInAiDubbingExpt}
          autoplay={autoplay}
          dubbedLanguages={getDubbedLanguagesList()}
          onDubbedAudioChoiceClick={onDubbedAudioChoiceClick}
          handleResolutionChangeForDubbedAudio={handleResolutionChangeForDubbedAudio}
          selectedAudioLanguage={selectedAudioLanguage}
          isOriginalAudioSelected={selectedAudioLanguage.languageKey === defaultLanguageKey}
          defaultLanguageKey={defaultLanguageKey}
          onToggleAutoplay={handleToggleSwitchClick}
          currentPlaybackRate={currentPlaybackRate}
          supportedPlaybackRates={supportedPlaybackRates}
          onPlaybackRateClick={onPlaybackRateClick}
          currentResolution={currentResolution}
          currentResolutionOption={currentResolutionOption}
          supportedResolutions={supportedResolutions}
          onResolutionOptionClick={onResolutionOptionClick}
          onSubtitleLanguageChoiceClick={onSubtitleLanguageChoiceClick}
          onSubtitleOffClick={onSubtitleOffClick}
          currentTrack={currentTrack}
          tracks={tracks}
          ref={settingsMenuPopupRef}
          visible={isSettingsMenuPopupVisible}
          closeSettingsMenu={closePopupMenu}
        />
      )}
    </>
  );
};

export default ControlBar;
